export const HERO_VIDEO = "https://estrelastudio.cdn.prismic.io/estrelastudio/aK8JRWGNHVfTOXgT_estrela-hero.mp4";
export const SHOWREEL_VIDEO = "https://estrelastudio.cdn.prismic.io/estrelastudio/aN6JiJ5xUNkB1alN_EstrelaShowreelPreviewV2.mp4";

export const PROJECTS = [
    {
        id: "wassen-lab",
        title: "Wassen Lab One",
        category: "Corporate Profile",
        image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?q=80&w=2070&auto=format&fit=crop",
        link: "https://wassenlabone.zxenxi.dev/",
        year: "2024",
        description: "A bold, Neubrutalist digital storefront for a premium shoe and apparel care service. Breaking away from clinical cleaning aesthetics, we embraced a raw, high-contrast visual language that speaks directly to sneaker culture.",
        challenge: "The traditional cleaning industry feels sterile and dated. Wassen Lab needed a brand identity that felt energetic and dominant to capture a younger demographic of collectors and enthusiasts who treat their shoes as assets, not just footwear.",
        solution: "I designed a high-impact interface using stark typography, vibrant reds, and heavy borders. The user journey is streamlined for quick price checking and service booking, with direct WhatsApp integration for immediate conversion. It's not just a cleaning service; it's a lifestyle brand.",
        technologies: ["React", "Tailwind CSS", "Framer Motion", "Vite"],
        process: [
            {
                phase: "01. Brand Strategy",
                desc: "We pivoted from 'cleaning service' to 'sneaker care lab'. This shifted the tone from apologetic to authoritative. I chose a Neubrutalist design system to mirror the raw, urban energy of street culture."
            },
            {
                phase: "02. Visual Identity",
                desc: "I curated a high-contrast palette: 'Alert Red' (#FF0000) against deep crimsons and blacks. Typography is intentionally oversized to create a poster-like feel on every scroll."
            },
            {
                phase: "03. Component Development",
                desc: "Developed a modular UI kit with reusable 'brutalist' components—sharp edges, thick borders, and distinct hover states. This ensures the brand feeling remains consistent across the pricing table, services list, and contact forms."
            },
            {
                phase: "04. Conversion Optimization",
                desc: "The primary goal was booking. I implemented sticky call-to-action buttons that follow the user, ensuring the path to 'Book Now' is never more than a thumb-tap away."
            }
        ],
        showcase: [
            "https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?q=80&w=1974&auto=format&fit=crop", // Detailed sneaker shot
            "https://images.unsplash.com/photo-1603808033192-082d6919d3e1?q=80&w=2015&auto=format&fit=crop", // Streetwear vibes
            "https://images.unsplash.com/photo-1556906781-9a412961d289?q=80&w=2003&auto=format&fit=crop"  // Clean minimalist texture
        ]
    },
    {
        id: "maspen-3d",
        title: "Maspen 3D Artist",
        category: "Creative Portfolio",
        image: "/images/maspen.jpeg",
        link: "https://maspen-3dartist.vercel.app/",
        year: "2024",
        description: "A sleek, dark-mode portfolio for Novendry Anggara Putra, a 3D Artist specializing in game animation and asset creation. The site serves as a digital gallery, prioritizing high-fidelity visual displays with a clean, tech-forward aesthetic.",
        challenge: "Artists need their work to do the talking. The challenge was to create a platform that could handle heavy 3D renders and animation reels without slowing down the user experience. The design needed to be invisible—a perfect frame for the art.",
        solution: "I built a Next.js powered application with an 'intelligent gallery' system. The dark, monochromatic theme enhances contrast, making the colorful 3D assets pop. Navigation is minimized to keep the focus on the viewport, creating a cinematic browsing experience.",
        technologies: ["Next.js", "React", "Tailwind CSS", "Framer Motion"],
        process: [
            {
                phase: "01. Asset Strategy",
                desc: "The core asset is the artwork. I implemented a tiered loading strategy for images, ensuring immediate layout stability while high-resolution textures load progressively."
            },
            {
                phase: "02. UI/UX Design",
                desc: "Adopted a 'Dark Industrial' aesthetic. Subtle grid lines and monospaced typography give it a technical, schematic feel that resonates with the game development industry."
            },
            {
                phase: "03. Gallery Architecture",
                desc: "The main gallery uses a masonry-style grid that adapts to different aspect ratios of renders. Each project opens in a focused view that isolates the artwork from UI distractions."
            }
        ],
        fullImage: "/images/maspen1.jpeg",
        showcase: [
            "/images/maspen-1.jpeg",
            "/images/maspen-2.png",
            "/images/maspen.jpeg"
        ]
    },
    {
        id: "travel-purworejo",
        title: "Travel Purworejo Executive",
        category: "Transportation Service",
        image: "https://images.unsplash.com/photo-1605218427368-35b869d80d28?q=80&w=2665&auto=format&fit=crop", // Modern Van / Highway vibe
        link: "https://travelpurworejo.vercel.app/",
        year: "2024",
        description: "A premium booking platform for an executive door-to-door travel service connecting Purworejo and Jabodetabek. The design prioritizes trust, comfort, and speed, transforming a standard shuttle service into a first-class road experience.",
        challenge: "The local travel industry suffers from low trust. Potential passengers often worry about old vehicles, uncertain schedules, and hidden costs. The client needed a digital presence that immediately communicated 'reliability' and 'luxury' to differentiate from budget competitors.",
        solution: "I crafted a glassmorphism-inspired interface that feels modern and transparent. The site features a clean schedule breakdown, detailed fleet showcase, and a friction-free booking flow via WhatsApp. The deep navy and vibrant orange palette creates a professional yet energetic brand identity.",
        technologies: ["React", "Vite", "Tailwind CSS", "Framer Motion"],
        process: [
            {
                phase: "01. Trust Architecture",
                desc: "Before pixels, we solved for trust. I designed a 'Guaranteed Departure' badge system and transparent fleet gallery that puts the hardware front and center, answering the user's first question: 'What am I riding in?'"
            },
            {
                phase: "02. Glassmorphism UI",
                desc: "To elevate the brand perception, I used a glassmorphism aesthetic—translucent cards over blurred backgrounds. This adds depth and a modern 'app-like' feel to the web experience."
            },
            {
                phase: "03. Conversion Funnel",
                desc: "The entire site is a funnel. Whether viewing the schedule or the fleet, the 'Book Now' action is context-aware. I streamlined the booking logic to pre-fill WhatsApp messages, reducing friction to zero."
            }
        ],
        showcase: [
            "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=2069&auto=format&fit=crop", // Interior / Bus seat vibe
            "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=2070&auto=format&fit=crop", // Highway / Tavel context
            "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2021&auto=format&fit=crop"  // Road trip / Scenic
        ]
    }
];

export const SERVICES = [
    {
        id: "01",
        title: "Product strategy and planning",
        desc: "Great products don't just happen. I translate complex business logic into scalable digital roadmaps, auditing your current stack to find high-ROI opportunities.",
        image: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?q=80&w=1000&auto=format&fit=crop"
    },
    {
        id: "02",
        title: "Bespoke web development",
        desc: "I build agency-grade platforms without the agency bloat. Using Next.js and React, I engineer interfaces that load in milliseconds and convert visitors into revenue.",
        image: "https://images.unsplash.com/photo-1605629921711-2f6b00c6bbf4?q=80&w=1000&auto=format&fit=crop"
    },
    {
        id: "03",
        title: "Brand identity & positioning",
        desc: "Why look like a template? I craft unique digital identities that command authority. From typography to motion design, I ensure your brand feels expensive and established.",
        image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?q=80&w=1000&auto=format&fit=crop"
    }
];

export const TESTIMONIALS = [
    { name: "Hendra Wijaya", role: "Owner, Wassen Lab", text: "We didn't just get a website; we got a digital flagship. The 'sneaker lab' aesthetic perfectly captured our vision. Bookings increased by 200% in the first month." },
    { name: "Novendry Anggara", role: "3D Artist", text: "My portfolio needed to be invisible so the art could stand out. zxenxi understood that immediately. The gallery loading speeds are incredible for such heavy assets." },
    { name: "Budi Santoso", role: "Manager, Travel Purworejo", text: "Trust is everything in our business. The new booking system and professional look have completely separated us from the budget travel competitors." },
    { name: "Sarah Jenkins", role: "Founder, Aura Health", text: "A rare combination of high-end design sensibility and rock-solid engineering. The new platform feels world-class." }
];

export const FAQS = [
    { q: "Why hire a solo developer instead of an agency?", a: "Direct accountability. You work directly with the senior engineer who writes your code, not a junior account manager. Zero bloat, faster turnaround, and elite-tier quality control." },
    { q: "Do you handle development?", a: "Yes. I am a full-stack developer. I design in Figma and build in code (React, Next.js, Node). This ensures nothing gets lost in translation between 'design' and 'dev'." },
    { q: "What is your typical timeline?", a: "Most high-impact branding and web projects run 6 to 8 weeks. Complex web applications typically take 8 to 12 weeks. I only take 2 clients at a time to ensure deep focus." },
    { q: "Do you work with international clients?", a: "Exclusively. While based in Indonesia, 90% of my partners are in the US, UK, and Europe. I operate at Silicon Valley standards with global availability." }
];
